import React, { useMemo } from "react";
import { FaGuitar, FaClock, FaMusic } from "react-icons/fa";
import clsx from "clsx";
import { type ChordProSong, type ChordProLine } from "../../lib/chordProParser";

interface Props {
  song: ChordProSong;
  activeChordIndex?: number;
  onChordClick?: (chord: string) => void;
  practiceMode?: boolean;
  currentChord?: string | null;
  detectedChord?: string | null;
}

/* ─── utilidades de directivas ─── */
function isDirectiveComment(text: string): string | null {
  const m = text.match(/^\{c(?:omment)?\s*:\s*(.*)\}$/i);
  return m ? m[1].trim() : null;
}
function isDirectiveStartOfChorus(text: string): boolean {
  return /^\{(?:soc|start_of_chorus)\}$/i.test(text.trim());
}
function isDirectiveEndOfChorus(text: string): boolean {
  return /^\{(?:eoc|end_of_chorus)\}$/i.test(text.trim());
}
function isDirectiveStartOfVerse(text: string): boolean {
  return /^\{(?:sov|start_of_verse)\}$/i.test(text.trim());
}
function isDirectiveEndOfVerse(text: string): boolean {
  return /^\{(?:eov|end_of_verse)\}$/i.test(text.trim());
}

/* ─── etiqueta de sección estilo Swiss ─── */
const SectionLabel: React.FC<{ text: string; accent?: boolean }> = ({
  text,
  accent,
}) => (
  <div className="flex items-center gap-2 my-5">
    <span
      className={clsx(
        "text-[10px] font-bold uppercase tracking-[0.18em]",
        accent ? "text-[#7B2335]" : "text-[#8A8078]"
      )}
    >
      {text}
    </span>
    <span className="flex-1 h-px bg-[#E5E5E0]" />
  </div>
);

export const ChordProPreview: React.FC<Props> = ({
  song,
  activeChordIndex,
  onChordClick,
  practiceMode,
  currentChord,
  detectedChord,
}) => {
  /* Chords usados, ordenados */
  const sortedChords = useMemo(
    () => Array.from(song.chords).sort(),
    [song.chords]
  );

  let inChorus = false;
  let inVerse = false;
  // Contador global de acordes para saber cuál es el activo por posición
  let chordCounter = -1;

  const renderLine = (line: ChordProLine, lineIdx: number) => {
    if (line.type === "empty") {
      return <div key={lineIdx} className="h-3" />;
    }

    if (line.type === "directive") {
      const text = line.segments[0].text;

      const comment = isDirectiveComment(text);
      if (comment) {
        return <SectionLabel key={lineIdx} text={comment} accent />;
      }

      if (isDirectiveStartOfChorus(text)) {
        inChorus = true;
        return <SectionLabel key={lineIdx} text="Coro" accent />;
      }
      if (isDirectiveEndOfChorus(text)) {
        inChorus = false;
        return null;
      }
      if (isDirectiveStartOfVerse(text)) {
        inVerse = true;
        return <SectionLabel key={lineIdx} text="Verso" />;
      }
      if (isDirectiveEndOfVerse(text)) {
        inVerse = false;
        return null;
      }

      return null;
    }

    /* ── ¿es una línea de SOLO acordes? ── */
    const hasChords = line.segments.some((s) => s.chord);
    const hasRealText = line.segments.some(
      (s) => s.text.trim().length > 0
    );
    const isChordOnlyLine = hasChords && !hasRealText;

    /* ── línea de solo acordes → fila horizontal espaciada ── */
    if (isChordOnlyLine) {
      return (
        <div key={lineIdx} className="flex flex-wrap items-center gap-x-4 gap-y-1 py-1">
          {line.segments
            .filter((s) => s.chord)
            .map((seg, segIdx) => {
              chordCounter++;
              const isActive = practiceMode && activeChordIndex === chordCounter;
              const isCorrect = isActive && detectedChord === seg.chord;
              const isWrong = isActive && detectedChord && detectedChord !== seg.chord;
              return (
                <button
                  key={segIdx}
                  onClick={() => onChordClick?.(seg.chord!)}
                  className={clsx(
                    "text-[15px] font-bold transition-colors cursor-pointer px-2 py-0.5 rounded-md",
                    isCorrect
                      ? "text-[#10B981] bg-[#10B981]/10"
                      : isWrong
                      ? "text-[#EF4444] bg-[#EF4444]/10"
                      : isActive
                      ? "text-[#92400E] bg-[#FEF3C7] border-2 border-[#F59E0B] animate-pulse"
                      : "text-[#7B2335] hover:text-[#5A1A28]"
                  )}
                  title={`Posición de ${seg.chord}`}
                >
                  {seg.chord}
                </button>
              );
            })}
        </div>
      );
    }

    /* ── lyric line normal → acorde flotante sobre primera sílaba ── */
    return (
      <div
        key={lineIdx}
        className={clsx(
          "leading-[2.4] text-[17px] text-[#1A1A1A] font-normal whitespace-pre",
          inChorus && "pl-4 border-l border-[#7B2335]/30"
        )}
      >
        {line.segments.map((seg, segIdx) => {
          if (seg.chord) {
            chordCounter++;
            const isActive = practiceMode && activeChordIndex === chordCounter;
            const isCorrect = isActive && detectedChord === seg.chord;
            const isWrong = isActive && detectedChord && detectedChord !== seg.chord;

            return (
              <span
                key={segIdx}
                className={clsx(
                  "relative inline-block align-baseline",
                  seg.text.trim() === "" && "min-w-[1.5em]"
                )}
              >
                {/* Indicador visual prominente del acorde activo */}
                {isActive && (
                  <span className="absolute -inset-x-1 -top-6 bottom-0 bg-[#FEF3C7] rounded-md border-2 border-[#F59E0B] animate-pulse z-0" />
                )}

                {/* Acorde flotante */}
                <button
                  onClick={() => onChordClick?.(seg.chord!)}
                  className={clsx(
                    "absolute -top-5 left-0 text-[13px] font-bold tracking-tight whitespace-nowrap cursor-pointer transition-colors z-10",
                    isCorrect
                      ? "text-[#10B981]"
                      : isWrong
                      ? "text-[#EF4444]"
                      : isActive
                      ? "text-[#92400E]"
                      : "text-[#7B2335] hover:text-[#5A1A28]"
                  )}
                  title={`Posición de ${seg.chord}`}
                >
                  {seg.chord}
                </button>

                {/* Letra (primera sílaba) */}
                <span
                  className={clsx(
                    "relative z-10",
                    isCorrect && "border-b-2 border-[#10B981]",
                    isWrong && "border-b-2 border-[#EF4444]",
                    isActive && !isCorrect && !isWrong && "font-semibold text-[#92400E]"
                  )}
                >
                  {seg.text || "\u00A0"}
                </span>

                {isWrong && detectedChord && (
                  <span className="absolute -bottom-4 left-0 text-[9px] text-[#EF4444] whitespace-nowrap font-medium z-10">
                    Detectado: {detectedChord}
                  </span>
                )}
              </span>
            );
          }
          return (
            <span key={segIdx} className="text-[#1A1A1A]">
              {seg.text}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 bg-white">
      {/* ═══════ HEADER ═══════ */}
      <header className="mb-10 pb-6 border-b border-[#E5E5E0]">
        <h1 className="text-[32px] font-bold tracking-tight text-[#1A1A1A] leading-none mb-2">
          {song.title || "Sin título"}
        </h1>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[#8A8078] uppercase tracking-wider font-medium">
          {song.artist && (
            <span className="flex items-center gap-1">
              <FaMusic className="w-3 h-3" />
              {song.artist}
            </span>
          )}
          {song.key && (
            <span className="flex items-center gap-1">
              <FaGuitar className="w-3 h-3" />
              Tono {song.key}
            </span>
          )}
          {song.capo > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-[#F5F5F0] text-[#4A4A4A] font-semibold">
              Capo {song.capo}
            </span>
          )}
          <span className="flex items-center gap-1">
            <FaClock className="w-3 h-3" />
            {song.tempo} BPM
          </span>
        </div>
      </header>

      {/* ═══════ CUERPO ═══════ */}
      <div className="space-y-0.5">
        {song.lines.map((line, i) => renderLine(line, i))}
      </div>

      {/* ═══════ TIMELINE DE ACORDES ═══════ */}
      {sortedChords.length > 0 && (
        <div className="mt-12 pt-6 border-t border-[#E5E5E0]">
          <SectionLabel text="Acordes" />

          {/* Grid suizo de acordes */}
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
            {sortedChords.map((chord) => (
              <button
                key={chord}
                onClick={() => onChordClick?.(chord)}
                className={clsx(
                  "group flex items-center justify-center",
                  "h-9 rounded border border-[#E5E5E0] bg-white",
                  "text-[13px] font-semibold text-[#4A4A4A]",
                  "hover:bg-[#F5F5F0] hover:border-[#7B2335]/40 hover:text-[#7B2335]",
                  "transition-all duration-150"
                )}
                title={`Ver diagrama de ${chord}`}
              >
                {chord}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
